import { observer } from "mobx-react";
import React, { useState } from "react";
import { generatePath, useHistory } from "react-router";

import { AssessmentAPI } from "@/api/legacy/risk-assessment/AssessmentApi";
import { QuestionnaireAPI } from "@/api/legacy/risk-assessment/QuestionnaireApi";
import { Icon } from "@/components/Elements";
import SlideMenu from "@/components/slideMenu/SlideMenu";
import CommentsSlideMenuContent from "@/components/table/shared/comments/CommentsSlideMenu/CommentsSlideMenuContent";
import SendRecordVersion from "@/components/table/shared/SendRecordVersion";
import { useMainStore } from "@/contexts/Store";
import {
  QuestionnaireRead,
  QuestionnaireStatus,
  ROUTES,
} from "@/features/risk-assessment";
import RowOptions, {
  Option,
} from "@/features/risk-assessment/components/RowOptions/RowOptions";
import ActionCell from "@/features/risk-assessment/components/Table/GenericTable/Cells/ActionCell";
import ContentCell from "@/features/risk-assessment/components/Table/GenericTable/Cells/ContentCell";
import Table from "@/features/risk-assessment/components/Table/GenericTable/Table";
import TableCellSpacer from "@/features/risk-assessment/components/Table/GenericTable/TableCellSpacer";
import TableHeaderCell from "@/features/risk-assessment/components/Table/GenericTable/TableHeaderCell";
import TableRow from "@/features/risk-assessment/components/Table/GenericTable/TableRow";
import { getQuestionnaireStatusLabel } from "@/features/risk-assessment/utils/questionnaire-helpers";
import { getDefinitionsFromScores } from "@/features/risk-assessment/utils/scoring-helpers";

import { AssessmentDetail } from "../../../types/assessment";
import { ScoreCell } from "../../Assessment/AssessmentScoring/ScoreCell";

type SubmittedQuestionnaireListProps = {
  questionnaires: Array<QuestionnaireRead>;
  assessment: AssessmentDetail;
  onRefreshAssessment: () => void;
};

const QUESTIONNAIRE_SUMMARY = "questionnaire_summary";
const LIVE_SHARE = "live_share";

const SubmittedQuestionnaireList = (props: SubmittedQuestionnaireListProps) => {
  const history = useHistory();
  const mainStore = useMainStore();
  const { id: userID } = mainStore.users.user;
  const { workspaceID, isAdmin, isIW } = mainStore.context;

  const [questionnaireID, setQuestionnaireID] = useState<number | undefined>();
  const [slideMenuState, setSlideMenuState] = useState<null | string>(null);

  const downloadQuestionnaire = (id: number) => {
    return AssessmentAPI.downloadAssessment(
      Number(workspaceID),
      props.assessment.id,
      id,
    );
  };

  const indexComments = (id: number) => {
    return mainStore.comments.index({ questionnaire_id: id });
  };

  const openQuestionnaireSummary = async (id: number) => {
    await indexComments(id);
    setSlideMenuState(QUESTIONNAIRE_SUMMARY);
    setQuestionnaireID(id);
  };

  const onReview = async (questionnaire: QuestionnaireRead) => {
    if (questionnaire.status == QuestionnaireStatus.READY_FOR_REVIEW) {
      await QuestionnaireAPI.setInReview(questionnaire.id);
      props.onRefreshAssessment();
    }

    history.push(
      generatePath(ROUTES.ASSESSMENTS_QUESTIONNAIRE_REVIEW_PATH, {
        assessmentId: props.assessment.id,
        questionnaireId: questionnaire.id,
        workspace_id: Number(workspaceID),
      }),
    );
  };

  const onUnlock = async (questionnaire: QuestionnaireRead) => {
    await QuestionnaireAPI.setInReview(questionnaire.id);
    props.onRefreshAssessment();
  };

  const openSlideMenuForLiveShare = (subQuestionnaireID: number) => {
    setQuestionnaireID(subQuestionnaireID);
    setSlideMenuState(LIVE_SHARE);
  };

  const shareRecordVersion = async (
    recordVersionId: number,
    workspaceIds: number[],
  ) => {
    try {
      if (questionnaireID) {
        await mainStore.recordVersions.share(
          questionnaireID,
          "Questionnaire",
          workspaceIds,
        );
      }
    } catch {
      mainStore.toast.setErrorText("Something went wrong");
    }
  };

  const generateOptions = (questionnaire: QuestionnaireRead): Option[] => {
    const options: Option[] = [];
    if (questionnaire.status === QuestionnaireStatus.FINALIZED) {
      options.push(
        {
          label: "View",
          onClick: () =>
            history.push(
              generatePath(ROUTES.ASSESSMENTS_QUESTIONNAIRE_REVIEW_PATH, {
                assessmentId: props.assessment.id,
                questionnaireId: questionnaire.id,
                workspace_id: Number(workspaceID),
              }),
            ),
        },
        {
          label: "Unlock Questionnaire",
          onClick: () => onUnlock(questionnaire),
        },
      );
    } else {
      const canAnswer =
        isAdmin ||
        questionnaire.reviewer_ids?.length === 0 ||
        (userID && questionnaire.reviewer_ids.includes(userID));
      options.push({
        label: "Review",
        disabled: !canAnswer,
        disabledTooltip: !canAnswer
          ? "You cannot review this internal questionnaire"
          : undefined,
        onClick: () => onReview(questionnaire),
      });
    }

    if (isIW) {
      options.push({
        label: "Share with workspace",
        onClick: () => openSlideMenuForLiveShare(questionnaire.id),
      });
    }

    options.push({
      label: "Add Summary",
      onClick: () => openQuestionnaireSummary(questionnaire.id),
    });

    options.push({
      label: "Export",
      onClick: () => downloadQuestionnaire(questionnaire.id),
    });

    return options;
  };

  return (
    <Table
      className="submitted-questionnaires-table"
      header={
        <>
          <TableHeaderCell
            title="Status"
            leftIcon={<Icon color="extrasBlueGrayDarker" name="folderLinear" />}
            firstDataHeader
          />
          <TableHeaderCell
            title="Name"
            leftIcon={<Icon color="extrasBlueGrayDarker" name="book1" />}
          />
          <TableHeaderCell
            title="Overall Risk"
            leftIcon={
              <Icon color="extrasBlueGrayDarker" name="priorityMedium" />
            }
          />
          <TableHeaderCell
            title="Inherent Questions"
            leftIcon={<Icon color="extrasBlueGrayDarker" name="number" />}
          />
          <TableHeaderCell
            title="Inherent Risk"
            leftIcon={
              <Icon color="extrasBlueGrayDarker" name="priorityMedium" />
            }
          />
          <TableHeaderCell
            title="Control Questions"
            leftIcon={<Icon color="extrasBlueGrayDarker" name="number" />}
          />
          <TableHeaderCell
            title="Control Risk"
            leftIcon={
              <Icon color="extrasBlueGrayDarker" name="priorityMedium" />
            }
          />
          <TableHeaderCell
            title="Residual Risk"
            leftIcon={
              <Icon color="extrasBlueGrayDarker" name="priorityMedium" />
            }
            lastDataHeader
          />
          <TableCellSpacer />
        </>
      }
    >
      {props.questionnaires.map((questionnaire, index) => {
        const {
          overallRiskRating,
          controlRiskRating,
          inherentRiskRating,
          residualRiskRating,
        } = getDefinitionsFromScores(
          // @ts-expect-error TS(2345) FIXME: Argument of type 'AssessmentScoring | undefined' i... Remove this comment to see the full error message
          questionnaire.scoring,
          props.assessment.risk_methodology,
        );
        return (
          <TableRow key={questionnaire.id} rowIndex={index}>
            <ContentCell
              content={getQuestionnaireStatusLabel(questionnaire.status, 1)}
              isFirstDataCell
            />
            <ContentCell content={questionnaire.name} />
            <ScoreCell
              rating={overallRiskRating}
              score={questionnaire.scoring?.overall_risk_score}
            />
            <ContentCell
              content={questionnaire.inherent_questions_count || "0"}
            />
            <ScoreCell
              rating={inherentRiskRating}
              score={questionnaire.scoring?.inherent_risk_score}
            />
            <ContentCell
              content={questionnaire.control_questions_count || "0"}
            />
            <ScoreCell
              rating={controlRiskRating}
              score={questionnaire.scoring?.control_risk_score}
            />
            <ScoreCell rating={residualRiskRating} isLastDataCell />

            <ActionCell>
              <RowOptions options={generateOptions(questionnaire)} />
            </ActionCell>
          </TableRow>
        );
      })}

      {slideMenuState && (
        <SlideMenu
          open={Boolean(slideMenuState)}
          closeSlideMenu={() => setSlideMenuState(null)}
        >
          {slideMenuState === QUESTIONNAIRE_SUMMARY && (
            <CommentsSlideMenuContent
              searchParams={{}}
              taskableType="QuestionnaireSummary"
              title="Summary"
              SubHeader={() => (
                <div className="section-commentary-subheader">
                  {
                    props.assessment.questionnaires.find(
                      ({ id }) => id === Number(questionnaireID),
                    )?.name
                  }
                </div>
              )}
              hideEmptyState
              closeSlideMenu={() => setSlideMenuState(null)}
            />
          )}
          {slideMenuState === LIVE_SHARE && (
            <SendRecordVersion
              recordVersionID={Number(questionnaireID)}
              recordName={"Questionnaire"}
              sendFunction={shareRecordVersion}
            />
          )}
        </SlideMenu>
      )}
    </Table>
  );
};

export default observer(SubmittedQuestionnaireList);
